/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDebounceCallback } from '@react-hook/debounce';
import { CircularProgress, Divider } from '@material-ui/core';
import _ from 'lodash';
import { StyledCode } from 'components/base/layout-components';
import axios from 'common/requester';
import {
  AUTH_LEVEL,
  GIT_PROVIDERS,
  PROVIDERS_MAP,
  mapPersonalAccessTokenErrorReason,
  validationStatusToMessageMap
} from 'common/constants/constants';
import { createSensorAuthorization, toggleModal } from 'state/callers';
import { myAccountSelector } from 'state/selectors/account';
import { showOAuthExplanationModal } from '../personalAccessToken.service';
import { getColor } from 'common/constants/_colors';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { useHistory } from 'react-router';
import AppRouter from 'common/AppRouter';
import { AUTH } from '@linearb/linjs-common';
import { getProviderConfig } from 'common/utils/provider.utils';
import MultipleTokensView from './MultipleTokens/MultipleTokensView';
import { sensorAuthorizationsByIdSelector } from 'state/selectors/sensors-authorizations';
import { StyledPatModalSubHeader } from './MultipleTokens/MultipleTokensView.style';
import useMultiTokens from './MultipleTokens/useMultiTokens';
import { boolPropsToClassNames } from 'common/utils/style.utils';
import useRequiredScopes from './hooks/useRequiredScopes';
import useTokenTypes from './hooks/useTokenTypes';
import TokenTypeSelector from './TokenTypeSelector/TokenTypeSelector';
import {
  LBAlertCircleIcon,
  LBButton,
  LBAlert,
  LBChevronLeftIcon,
  LBCheckIcon,
  LBInput
} from '@linearb/buzz';
import GitTypes from 'common/constants/GitTypes';

const DividerStyled = styled(Divider)`
  background-color: ${getColor('botticelli')} !important;
  height: 100% !important;
`;

const DividerWrapper = styled.div`
  height: 100%;

  ::after {
    position: absolute;
    top: 50%;
    margin-left: -0.7rem;
    font-size: 1.2rem;
    background-color: white;
    padding: 0.7rem 0;
  }
  &.withOR {
    ::after {
      content: 'OR';
    }
  }
`;

// eslint-disable-next-line max-lines-per-function
const PersonalAccessTokenModal = ({
  provider,
  createSensorAuthorization,
  isCloudProvider = false,
  withPAT = true,
  // @ts-ignore
  baseUrl = getProviderConfig({ provider, isCloudProvider }).baseUrl,
  onSubmit = _.noop,
  onBack = _.noop,
  isTeamLicense,
  sensorAuthId,
  alwaysShowBack = false,
  toggleModal,
  azureOrganizationName = '',
  authorizationLevel = 'organization'
}) => {
  const providerConfig = getProviderConfig({
    provider,
    isCloudProvider
  }) as any;
  const history = useHistory();

  const integrationsById = useSelector(sensorAuthorizationsByIdSelector);
  const integration = integrationsById[sensorAuthId];
  const multiTokens = useMultiTokens({ integration });
  const tokenTypes = useTokenTypes({ providerName: provider });

  const [loading, setLoading] = useState(false);
  const [apiTestSuccessfully, setApiTestSuccessfully] = useState(false);
  const [failurePayload, setFailurePayload] = useState(null);
  const [inExplanationMode, setInExplanationMode] = useState(!withPAT);
  const [accessToken, setAccessToken] = useState('');
  const [orgNameValue, setOrgNameValue] = useState(azureOrganizationName);
  const [orgName, setOrgName] = useState(azureOrganizationName);
  const [cacheId, setCacheId] = useState(null);
  const [username, setUsername] = useState('');
  const requiredScopes = useRequiredScopes({
    provider,
    authorizationLevel,
    tokenType: tokenTypes.selected
  });
  const hasOrgName = providerConfig.showOrgNameField;
  const tokenDisabled = hasOrgName ? loading || orgNameValue === '' : loading;
  const testApiConnectivityEnabled = hasOrgName
    ? accessToken.length && orgName.length
    : accessToken.length;
  const baseUrlForTest = providerConfig.baseUrlForTest
    ? { base_url: providerConfig.baseUrlForTest(baseUrl, orgName) }
    : {};
  const authTypeText =
    sensorAuthId && !multiTokens.isEnabled ? `Reauthorize` : `Connect`;
  const tokenName =
    tokenTypes.selectedOption?.name.toLowerCase() || 'Personal Access Token';

  useEffect(() => {
    setAccessToken(_.trim(accessToken));
    setApiTestSuccessfully(false);
    if (testApiConnectivityEnabled) {
      testApiConnectivity();
    } else {
      setApiTestSuccessfully(false);
    }
  }, [accessToken, orgName]);

  const cacheToken = async () => {
    toggleModal();
    history.push({
      pathname: AppRouter.REPOSITORIES.path,
      search: `?${AUTH.OAUTH_SECRET_CACHE_ID}=${cacheId}&sensor_auth_id=${sensorAuthId}`
    });
  };
  const createSensor = async () => {
    // post sensor authorization
    const body = {
      sensor_id: GIT_PROVIDERS[provider.toUpperCase()],
      state: 'ok',
      token: {
        token: accessToken,
        ...(!isCloudProvider ? { self_hosted_uri: _.trim(baseUrl, '/') } : {}),
        refresh_token: null,
        is_proxied: !isCloudProvider
      },
      profile: {
        username,
        ...baseUrlForTest
      },
      name: hasOrgName
        ? `${providerConfig.name} - ${orgName}`
        : authorizationLevel === AUTH_LEVEL.INDIVIDUAL
        ? `${providerConfig.name} - ${AUTH_LEVEL.INDIVIDUAL}`
        : providerConfig.name,
      authorization_level: authorizationLevel
    };
    const sensorAuth = await createSensorAuthorization(body);
    if (!sensorAuth) {
      const error = { errMessage: 'could not create sensor authorization' };
      onSubmit({ isPAT: true, provider, sensorAuth, error });
      return;
    }

    onSubmit({ isPAT: true, provider, sensorAuth });
  };

  const testApiConnectivity = useDebounceCallback(async () => {
    const providerConfig = getProviderConfig({ provider, isCloudProvider });
    const body = {
      ...(!isCloudProvider ? { self_hosted_uri: baseUrl } : {}),
      access_token: accessToken,
      ...baseUrlForTest,
      ...(sensorAuthId ? { cacheCredentials: true } : {})
    };

    setFailurePayload(null);
    setLoading(true);

    const {
      token_status,
      token_scopes,
      required_scopes,
      missing_scopes,
      username,
      cacheKey,
      reason
    } = (await axios.post((providerConfig as any).apiTestUrl, body)) as any;

    if (token_status === validationStatusToMessageMap.VALID) {
      setTimeout(() => {
        setLoading(false);
        setUsername(username);
        setApiTestSuccessfully(true);
        setCacheId(cacheKey);
      }, 2000);

      return;
    }

    const errReason = _.get(mapPersonalAccessTokenErrorReason, reason, '');

    setTimeout(() => {
      setFailurePayload({
        message: `${validationStatusToMessageMap[token_status]} ${errReason}`,
        token_scopes,
        required_scopes,
        missing_scopes
      });

      setApiTestSuccessfully(false);
      setLoading(false);
    }, 2000);
  }, 1000);

  const renderPersonalAccessTokenTest = ({ withTitle }) => {
    const providerConfig = getProviderConfig({
      provider,
      isCloudProvider
    }) as any;

    return (
      <div className='flex flex-col flex-grow basis-0 pl-10'>
        {withTitle ? (
          <StyledPatModalSubHeader>
            {authTypeText} with Access Token
          </StyledPatModalSubHeader>
        ) : null}
        {tokenTypes.isEnabled && (
          <TokenTypeSelector
            options={tokenTypes.options}
            value={tokenTypes.selected}
            onChange={tokenTypes.setSelected}
          />
        )}
        <div className='flex flex-col gap-2'>
          <ol className='text-base flex flex-col gap-2'>
            <li>
              <a
                className='underline'
                href={providerConfig.infoLink}
                target='__blank'
                rel='noreferrer'
              >
                Create a {tokenName}
              </a>{' '}
              on {providerConfig.name}
              {providerConfig.name === 'GitLab' && (
                <>
                  <br />
                  Set the service account as <strong>Maintainer</strong> or{' '}
                  <strong>Owner</strong>.
                </>
              )}
            </li>
            <li className='scopes'>
              Ensure the required scopes are checked:
              <div className='max-w-full flex flex-row flex-wrap'>
                {requiredScopes?.map?.((scope, idx) => (
                  <StyledCode key={`${idx}li`}>{scope}</StyledCode>
                ))}
              </div>
            </li>
            {hasOrgName && (
              <li>
                <LBInput
                  className='w-5/6'
                  data-cy='organizationInput'
                  disabled={loading || sensorAuthId}
                  type='text'
                  name='organization'
                  placeholder='Enter organization name'
                  value={orgNameValue}
                  onChange={event => setOrgNameValue(event.target.value)}
                  onBlur={() => setOrgName(orgNameValue)}
                />
              </li>
            )}
            <li>
              <div className='flex gap-2'>
                <LBInput
                  className='w-5/6'
                  data-cy='patInput'
                  disabled={tokenDisabled}
                  type='password'
                  autoComplete='off'
                  name='access-token'
                  placeholder={`Enter ${tokenName}`}
                  onChange={event => setAccessToken(event.target.value)}
                  value={accessToken}
                />
                {loading && <CircularProgress size={26} />}
                {!loading && apiTestSuccessfully && (
                  <LBCheckIcon className='size-6 text-success' />
                )}
              </div>
            </li>
          </ol>

          <div>
            <LBButton
              data-cy={sensorAuthId ? 'reauthorize' : 'connect'}
              size='lg'
              disabled={!apiTestSuccessfully || multiTokens.isLoading}
              onClick={
                multiTokens.isEnabled
                  ? () => {
                      multiTokens.createToken({
                        token: accessToken,
                        username
                      });
                      setAccessToken('');
                    }
                  : sensorAuthId
                  ? cacheToken
                  : createSensor
              }
            >
              {authTypeText}
            </LBButton>
            {failurePayload && (
              <LBAlert variant='destructive' className='my-2'>
                <LBAlertCircleIcon />
                {provider === GitTypes.AZURE_DEVOPS ? (
                  <p>
                    We couldn’t connect to Azure because your Personal Access
                    Token is missing required scopes. Please update your token’s
                    permissions and try again. For more information,{' '}
                    <a
                      className='underline'
                      target='_blank'
                      rel='noreferrer'
                      href='https://linearb.helpdocs.io/article/3jkmufmduv-connect-azure-dev-ops-using-a-personal-access-token'
                    >
                      see our documentation
                    </a>
                    .
                  </p>
                ) : (
                  <p>
                    {failurePayload.message}
                    <br />
                    {failurePayload.missing_scopes ? (
                      <>
                        {failurePayload.missing_scopes.join(', ')}
                        <br />
                        please add them and try again
                      </>
                    ) : (
                      ''
                    )}
                  </p>
                )}
              </LBAlert>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOAuth = () => {
    return (
      <div className='flex flex-col gap-2 flex-grow basis-0'>
        <h2 className='text-lg'>{authTypeText} as an Admin</h2>
        <p className='text-base'>
          Connect with OAuth. This is the fastest path
          <br />
          but requires administrative access.
        </p>
        <div className='mt-2'>
          <LBButton
            size='lg'
            data-cy='oauth_authorize'
            onClick={() => {
              setInExplanationMode(true);
              setAccessToken('');
              setFailurePayload(null);
              setApiTestSuccessfully(false);
            }}
          >
            {`${sensorAuthId ? `Reauthorize` : `Authorize`}`}{' '}
            {PROVIDERS_MAP[provider].displayName || _.capitalize(provider)}
          </LBButton>
        </div>
      </div>
    );
  };

  const ExplanationBackComponent = withPAT ? (
    <LBButton variant='ghost' onClick={() => setInExplanationMode(false)}>
      <LBChevronLeftIcon /> {authTypeText} with personal access token
    </LBButton>
  ) : !sensorAuthId ? (
    <LBButton variant='ghost' onClick={onBack}>
      <LBChevronLeftIcon /> Connect to a different Git provider
    </LBButton>
  ) : null;

  if (inExplanationMode) {
    return showOAuthExplanationModal(
      provider,
      ExplanationBackComponent,
      provider => onSubmit({ isPAT: false, provider }),
      !!sensorAuthId
    );
  }

  const Logo = providerConfig.logo;

  return (
    <div className='flex flex-col h-full'>
      <div className='flex flex-col px-8 py-4 h-full'>
        <div className='flex gap-2 items-center'>
          <Logo className='size-4' />

          <h1 className='text-xl'>
            {multiTokens.isEnabled
              ? `Manage Tokens: ${integration.name}`
              : sensorAuthId
              ? `Reauthorize ${providerConfig.name} integration`
              : `Connect to ${providerConfig.name}`}
          </h1>
        </div>
        <div className='flex pt-8'>
          {multiTokens.isEnabled ||
          (isCloudProvider && providerConfig.showOauthSection) ? (
            <>
              {multiTokens.isEnabled ? (
                <MultipleTokensView multiTokens={multiTokens} />
              ) : (
                renderOAuth()
              )}
              <DividerWrapper
                className={boolPropsToClassNames({
                  withOR: !multiTokens.isEnabled
                })}
              >
                <DividerStyled orientation='vertical' flexItem />
              </DividerWrapper>
              {renderPersonalAccessTokenTest({ withTitle: isCloudProvider })}
            </>
          ) : (
            renderPersonalAccessTokenTest({ withTitle: isCloudProvider })
          )}
        </div>
      </div>
      {alwaysShowBack || (!isTeamLicense && !sensorAuthId) ? (
        <div className='px-2 py-4'>
          <LBButton variant='ghost' onClick={onBack}>
            <LBChevronLeftIcon /> Connect to a different Git provider
          </LBButton>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const { organization } = state;

  return {
    account: myAccountSelector(state),
    organization,
    isTeamLicense: isTeamLicenseSelector(state)
  };
};

const mapDispatchToProps = { createSensorAuthorization, toggleModal };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PersonalAccessTokenModal);
